/**
 * One keyword glyph as inline SVG. Stroke scales with the box so the line
 * weight stays even from an 11px card badge to a 28px HELP entry.
 */

import type { Keyword } from '@/engine';
import { KEYWORD_GLYPHS } from './paths';

export interface KeywordIconProps {
  keyword: Keyword;
  size?: number;
  /** Any CSS color. Defaults to currentColor so it inherits. */
  color?: string;
  className?: string;
  /** Set when the icon stands alone and needs a name for screen readers. */
  title?: string;
}

export function KeywordIcon({ keyword, size = 14, color, className, title }: KeywordIconProps) {
  const glyph = KEYWORD_GLYPHS[keyword];
  if (!glyph) return null;
  const stroke = Math.max(1.3, 24 / size + 0.6);
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke={color ?? 'currentColor'}
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      {glyph.d.map((d, i) => (
        <path key={i} d={d} />
      ))}
      {(glyph.dots ?? []).map(([cx, cy, r], i) => (
        <circle key={`dot-${i}`} cx={cx} cy={cy} r={r} fill={color ?? 'currentColor'} stroke="none" />
      ))}
    </svg>
  );
}
